import type { MatchState, PlayerState } from "@arcade-hockey/sim";

// Returns true if `player` is the closest teammate (excluding goalies — they
// aren't in state.players) to the point (targetX, targetY) compared with the
// other skaters on the same team. Tie-breaks toward "true" — when two
// teammates are equidistant, both get this answer; the canonical sort order
// of state.players (by id) makes that stable from the caller's perspective.
//
// Zero-alloc: just a single O(skaters) scan over state.players. Used by the
// defender role (to pick the pressurer vs cover) and by the chaser branch
// (to pick the closest-to-puck chaser vs everyone else who should anchor).
export function isClosestTeammateTo(
  player: PlayerState,
  targetX: number,
  targetY: number,
  state: MatchState,
): boolean {
  const dx = player.pos.x - targetX;
  const dy = player.pos.y - targetY;
  const myDist = dx * dx + dy * dy;
  for (const teammate of state.players) {
    if (teammate.teamId !== player.teamId) continue;
    if (teammate.id === player.id) continue;
    const tdx = teammate.pos.x - targetX;
    const tdy = teammate.pos.y - targetY;
    if (tdx * tdx + tdy * tdy < myDist) return false;
  }
  return true;
}

// Returns the player's rank (1-indexed) among teammates by distance to a
// point. 1 = closest, 2 = second closest, etc. Used by the chaser branch
// to route F2 to a loose-puck trailer position instead of full anchor.
export function teammateRankByDistance(
  player: PlayerState,
  targetX: number,
  targetY: number,
  state: MatchState,
): number {
  const dx = player.pos.x - targetX;
  const dy = player.pos.y - targetY;
  const myDist = dx * dx + dy * dy;
  let rank = 1;
  for (const teammate of state.players) {
    if (teammate.teamId !== player.teamId) continue;
    if (teammate.id === player.id) continue;
    const tdx = teammate.pos.x - targetX;
    const tdy = teammate.pos.y - targetY;
    if (tdx * tdx + tdy * tdy < myDist) rank += 1;
  }
  return rank;
}
